import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useRegistration } from '@/context/RegistrationContext';
import { WregException } from '@/components/webreg/screens/WregException';
import {
  WREG_EXCEPTIONS,
  type WregExceptionCopy,
  type WregExceptionStep,
} from '@/components/webreg/screens/wregExceptions';
import { useWregFlowActions } from '@/components/webreg/WregFlowProvider';
import { useWregDev } from '@/components/webreg/dev/WregDevContext';
import { wregAlignNoteligBodyKey } from '@/components/webreg/wregAlignment';
import { wregNoteligBodyKey } from '@/components/webreg/wregEligibility';
import { EVENT_SOURCES, SELECT_ERROR_RETRY } from '@/consts/events';
import { useAnalytics } from '@/hooks/useAnalytics';

type CtaDeps = {
  t: TFunction;
  copy: WregExceptionCopy;
  canRetry: boolean;
  onRetryRun: () => Promise<void>;
  onRetryDone: () => void;
  onHelp: () => void;
  onAckToAllSet: () => void;
  onReenterDetails: () => void;
  onRestartFlow: () => void;
  onStartOver: () => void;
};

export const wregExceptionCtas = (step: WregExceptionStep, d: CtaDeps) => {
  const restart = { secondary: d.t('wreg.x.restart'), onSecondary: d.onRestartFlow };
  const support = { primary: d.t(d.copy.primaryKey), onPrimary: d.onHelp };
  if (step === 'x-error') {
    if (d.canRetry)
      return {
        primary: d.t('wreg.x.error.retry'),
        loadPrimary: true,
        onPrimaryClick: d.onRetryRun,
        onPrimary: d.onRetryDone,
        secondary: d.t(d.copy.primaryKey),
        onSecondary: d.onHelp,
      };
    return { ...support, ...restart };
  }
  if (step === 'x-labspending')
    return { primary: d.t(d.copy.primaryKey), onPrimary: d.onAckToAllSet, ...restart };
  if (step === 'x-switchok')
    return { primary: d.t(d.copy.primaryKey), onPrimary: d.onAckToAllSet };
  if (step === 'x-mismatch')
    return { primary: d.t(d.copy.primaryKey), onPrimary: d.onReenterDetails };
  if (step === 'x-declined')
    return { primary: d.t(d.copy.primaryKey), onPrimary: d.onStartOver };
  return support;
};

const WregExceptionRoute = ({ step }: { step: WregExceptionStep }) => {
  const { t } = useTranslation();
  const { registrationData } = useRegistration();
  const dev = useWregDev();
  const { track } = useAnalytics();
  const {
    retryStage,
    errorStage,
    retryFromError,
    afterRetry,
    setHelpOpen,
    onAckToAllSet,
    reenterDetails,
    restartFlow,
  } = useWregFlowActions();

  const copy = WREG_EXCEPTIONS[step];
  const { alignCode, eligCode } = registrationData.wreg;
  const result = eligCode ?? dev.mockCode;

  const canRetry = retryStage !== null;
  const noteligBodyKey = alignCode
    ? wregAlignNoteligBodyKey(alignCode)
    : wregNoteligBodyKey(result);

  const ctas = wregExceptionCtas(step, {
    t,
    copy,
    canRetry,
    onRetryRun: () => {
      track({ event: SELECT_ERROR_RETRY, properties: { error_stage: errorStage } });
      return retryFromError();
    },
    onRetryDone: afterRetry,
    onHelp: () => setHelpOpen(true, EVENT_SOURCES.SCREEN),
    onAckToAllSet,
    onReenterDetails: reenterDetails,
    onRestartFlow: restartFlow,
    onStartOver: restartFlow,
  });

  return (
    <WregException
      tone={copy.tone}
      title={t(copy.titleKey)}
      body={t(step === 'x-notelig' ? noteligBodyKey : copy.bodyKey)}
      {...ctas}
    />
  );
};

export { WregExceptionRoute };
